import {
  DeviceResponseSchema,
  DocumentLinkSchema,
  NotificationListSchema,
  NotificationPreferencesSchema,
  NotificationResponseSchema,
  ReadAllResponseSchema,
  type RegisterDevice,
  TestReminderResponseSchema,
  UnsubscribeInfoSchema,
  type UpdateNotificationPreferences,
} from '@oneapp/shared';
import { z } from 'zod';
import { api } from '../lib/api';

const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});
const Empty = z.unknown();

/** Notices, reminder settings, browsers that get push, one-tap unsubscribe and the PDF records (Phase 13). */
export const notificationsApi = {
  list: (cursor: string | null, signal?: AbortSignal) =>
    api.get(`/api/v1/notifications${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`, NotificationListSchema, {
      auth: true,
      ...withSignal(signal),
    }),

  /** Just the count, for the bell. */
  unread: (signal?: AbortSignal) =>
    api.get('/api/v1/notifications?limit=1', NotificationListSchema, { auth: true, ...withSignal(signal) }),

  read: (id: string) =>
    api.post(`/api/v1/notifications/${encodeURIComponent(id)}/read`, NotificationResponseSchema, { auth: true }),

  readAll: () => api.post('/api/v1/notifications/read-all', ReadAllResponseSchema, { auth: true }),

  preferences: (signal?: AbortSignal) =>
    api.get('/api/v1/me/notification-preferences', NotificationPreferencesSchema, {
      auth: true,
      ...withSignal(signal),
    }),

  update: (body: UpdateNotificationPreferences) =>
    api.put('/api/v1/me/notification-preferences', NotificationPreferencesSchema, { auth: true, body }),

  test: () => api.post('/api/v1/me/notification-preferences/test', TestReminderResponseSchema, { auth: true }),

  registerDevice: (body: RegisterDevice) => api.post('/api/v1/me/devices', DeviceResponseSchema, { auth: true, body }),

  removeDevice: (id: string) => api.delete(`/api/v1/me/devices/${encodeURIComponent(id)}`, Empty, { auth: true }),

  /** No sign-in: the link in an email says whose reminders. */
  unsubscribeInfo: (token: string, signal?: AbortSignal) =>
    api.get(`/api/v1/unsubscribe?token=${encodeURIComponent(token)}`, UnsubscribeInfoSchema, withSignal(signal)),

  unsubscribe: (token: string) =>
    api.post(`/api/v1/unsubscribe?token=${encodeURIComponent(token)}`, UnsubscribeInfoSchema, {}),

  filingRecord: (filingId: string) =>
    api.get(`/api/v1/filings/${encodeURIComponent(filingId)}/acknowledgement`, DocumentLinkSchema, { auth: true }),

  challanRecord: (challanId: string) =>
    api.get(`/api/v1/challans/${encodeURIComponent(challanId)}/record`, DocumentLinkSchema, { auth: true }),
};

export const notificationKeys = {
  all: ['notifications'] as const,
  unread: ['notifications', 'unread'] as const,
  preferences: ['notification-preferences'] as const,
  unsubscribe: (token: string) => ['unsubscribe', token] as const,
};
